import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Inbox, Loader2 } from "lucide-react";
import { rememberLanguage } from "../lib/language";

export const inputClass =
  "w-full rounded-xl border border-app-line bg-surface p-3 text-base text-app-ink placeholder:text-app-icon focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20";
export const textareaClass = `${inputClass} min-h-24 resize-y`;
export const primaryButtonClass =
  "w-full rounded-xl bg-accent px-4 py-3 text-base font-semibold text-content-on-accent shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-app-field disabled:text-app-icon disabled:shadow-none";
export const secondaryButtonClass =
  "w-full rounded-xl bg-app-field px-4 py-3 text-base font-semibold text-accent transition-colors hover:bg-accent-soft disabled:cursor-not-allowed disabled:text-app-icon";
export const ghostButtonClass =
  "flex items-center gap-1 text-sm font-medium text-accent hover:text-accent-hover";
export const outlineButtonClass =
  "rounded-xl border border-app-line px-3 py-2 text-sm font-medium text-app-ink disabled:opacity-40";

export type StatusVariant = "empty" | "error" | "loading" | "success";

export function Status({
  title,
  message,
  variant = "empty",
}: {
  title: string;
  message: string;
  variant?: StatusVariant;
}) {
  const Icon =
    variant === "error"
      ? AlertTriangle
      : variant === "loading"
        ? Loader2
        : variant === "success"
          ? CheckCircle2
          : Inbox;
  const iconClass =
    variant === "error"
      ? "bg-danger-bg text-accent-strong"
      : variant === "success"
        ? "bg-success-bg text-success-text"
        : "bg-app-field text-app-muted";
  return (
    <section className="grid justify-items-center gap-2 px-5 py-16 text-center">
      <div className={`grid h-12 w-12 place-items-center rounded-full ${iconClass}`}>
        <Icon size={22} className={variant === "loading" ? "animate-spin" : ""} />
      </div>
      <h2 className="text-lg font-medium text-app-ink">{title}</h2>
      <p className="text-sm text-app-copy">{message}</p>
    </section>
  );
}

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  return (
    <div
      className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-app-field p-1 text-xs font-bold shadow-sm"
      role="group"
      aria-label={t("language")}
      dir="ltr"
    >
      <button
        type="button"
        className={`flex h-full items-center rounded-full px-3 transition-colors ${
          i18n.language === "en"
            ? "bg-accent text-content-on-accent shadow-sm"
            : "text-app-muted hover:text-app-ink"
        }`}
        onClick={() => {
          rememberLanguage("en");
          void i18n.changeLanguage("en");
        }}
        aria-pressed={i18n.language === "en"}
      >
        EN
      </button>
      <button
        type="button"
        // Jameel Noori Nastaleeq is a calligraphic Nastaliq face — its
        // diagonal, stacked letterforms need real body-text size to render
        // cleanly. At this chip's small size it comes out broken, so this
        // label is pinned to a plain sans font regardless of app language.
        style={{ fontFamily: "system-ui, sans-serif" }}
        className={`flex h-full items-center rounded-full px-3 transition-colors ${
          i18n.language === "ur"
            ? "bg-accent text-content-on-accent shadow-sm"
            : "text-app-muted hover:text-app-ink"
        }`}
        onClick={() => {
          rememberLanguage("ur");
          void i18n.changeLanguage("ur");
        }}
        aria-pressed={i18n.language === "ur"}
      >
        اردو
      </button>
    </div>
  );
}

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "success" | "warning" | "live";
  children: ReactNode;
}) {
  const tones = {
    neutral: "bg-app-field text-app-copy",
    success: "bg-success-bg text-success-text",
    warning: "bg-warning-bg text-warning-text",
    live: "bg-success text-content-on-success",
  };
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
